import { useEffect, useRef } from "react";

// Convert a hex string to an rgba() with the given alpha.
function hexA(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Animated network mesh behind the hero — larger "system" nodes (PMS, Fonio,
 * Front Desk, Billing, Housekeeping, Channel) connected to drifting data
 * nodes, with accent-colored particles that travel along the edges.
 *
 * Runs on a <canvas> inside a masked container so the edges fade into the
 * page background.
 */
export function HeroAmbient() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      w = parent.offsetWidth;
      h = parent.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const css = getComputedStyle(document.documentElement);
    const accent = css.getPropertyValue("--accent").trim() || "#3BB0F5";

    const rand = (a: number, b: number) => a + Math.random() * (b - a);
    const systems = [
      { label: "PMS" },
      { label: "KOVA" },
      { label: "Front Desk" },
      { label: "Billing" },
      { label: "Housekeeping" },
      { label: "Channel" },
    ];

    type Node = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      isSystem: boolean;
      label: string | null;
      phase: number;
    };

    const nodes: Node[] = [];
    const N = 46;
    for (let i = 0; i < N; i++) {
      const isSystem = i < systems.length;
      nodes.push({
        x: rand(0.05, 0.95),
        y: rand(0.05, 0.95),
        vx: rand(-0.00006, 0.00006),
        vy: rand(-0.00006, 0.00006),
        r: isSystem ? rand(4.5, 6) : rand(1.2, 2.2),
        isSystem,
        label: isSystem ? systems[i].label : null,
        phase: Math.random() * Math.PI * 2,
      });
    }

    type Particle = { from: number; to: number; t: number; speed: number };
    const particles: Particle[] = [];
    const spawnParticle = () => {
      if (particles.length > 18) return;
      const from = Math.floor(Math.random() * systems.length);
      let to: number;
      do {
        to = Math.floor(Math.random() * nodes.length);
      } while (to === from);
      particles.push({ from, to, t: 0, speed: rand(0.004, 0.009) });
    };
    const particleTimer = window.setInterval(spawnParticle, 260);

    let raf = 0;
    const start = performance.now();

    const render = (now: number) => {
      const t = (now - start) / 1000;
      ctx.clearRect(0, 0, w, h);

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0.02 || n.x > 0.98) n.vx *= -1;
        if (n.y < 0.02 || n.y > 0.98) n.vy *= -1;
      }

      const toPx = (n: Node) => ({ x: n.x * w, y: n.y * h });

      // Edges — denser near system nodes, sparse between data points.
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const ap = toPx(a);
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const bp = toPx(b);
          const dx = ap.x - bp.x;
          const dy = ap.y - bp.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          const thresh = a.isSystem || b.isSystem ? 260 : 140;
          if (d < thresh) {
            const alpha = (1 - d / thresh) * (a.isSystem || b.isSystem ? 0.22 : 0.09);
            ctx.strokeStyle = `rgba(11,15,20,${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(ap.x, ap.y);
            ctx.lineTo(bp.x, bp.y);
            ctx.stroke();
          }
        }
      }

      // Particles travelling edge→node.
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.t += p.speed;
        if (p.t >= 1) {
          particles.splice(i, 1);
          continue;
        }
        const a = toPx(nodes[p.from]);
        const b = toPx(nodes[p.to]);
        const x = a.x + (b.x - a.x) * p.t;
        const y = a.y + (b.y - a.y) * p.t;
        const grad = ctx.createRadialGradient(x, y, 0, x, y, 14);
        grad.addColorStop(0, hexA(accent, 0.9));
        grad.addColorStop(1, hexA(accent, 0));
        ctx.fillStyle = grad;
        ctx.fillRect(x - 14, y - 14, 28, 28);
        ctx.fillStyle = accent;
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Nodes — system nodes with a pulsing glow + label, data nodes as dots.
      for (const n of nodes) {
        const p = toPx(n);
        if (n.isSystem) {
          const pulse = 0.6 + 0.4 * Math.sin(t * 2 + n.phase);
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 30);
          grad.addColorStop(0, hexA(accent, 0.22 * pulse));
          grad.addColorStop(1, hexA(accent, 0));
          ctx.fillStyle = grad;
          ctx.fillRect(p.x - 30, p.y - 30, 60, 60);
          ctx.fillStyle = accent;
          ctx.beginPath();
          ctx.arc(p.x, p.y, n.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.font = '500 10px "Geist", system-ui';
          ctx.fillStyle = "rgba(11,15,20,0.55)";
          ctx.textAlign = "center";
          ctx.fillText(n.label ?? "", p.x, p.y - 12);
        } else {
          ctx.fillStyle = "rgba(11,15,20,0.35)";
          ctx.beginPath();
          ctx.arc(p.x, p.y, n.r, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(particleTimer);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="hero-ambient" aria-hidden="true">
      <canvas ref={canvasRef} />
      <div className="ambient-grid" />
    </div>
  );
}
